/* W8.1 — Décisions du Deal (Decision Economics Engine v1, Platform).
 * Journal : savoir → estimer → décider (snapshot immuable) → outcome. */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import { Scale3d, Plus, Trash2, CheckCircle2, Flag, History } from "lucide-react";
import { ReestimateForm, EstimationHistory } from "@/components/decisions/ReestimateForm";
import { MemoryRecallPanel } from "@/components/MemoryRecallPanel";

const fmtCHF = (v) => new Intl.NumberFormat("fr-CH", { style: "currency", currency: "CHF", maximumFractionDigits: 0 }).format(v || 0);
const VERDICT_STYLE = { PURSUE: "border-[#1D9E75]/50 text-[#1D9E75] bg-[#1D9E75]/10", HOLD: "border-[#EF9F27]/50 text-[#EF9F27] bg-[#EF9F27]/10", PASS: "border-[#E24B4A]/50 text-[#E24B4A] bg-[#E24B4A]/10" };
const METHODS = ["expert", "historical", "model", "market", "autre"];
const emptyScenario = () => ({ label: "", payoff_chf: 0, probability: { value: 0.5, source: "", method: "expert", confidence: 0.5 } });

function DecisionForm({ dealId, onDone }) {
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [cost, setCost] = useState(0);
  const [capital, setCapital] = useState("");
  const [coc, setCoc] = useState(0);
  const [marketP, setMarketP] = useState("");
  const [scenarios, setScenarios] = useState([emptyScenario(), emptyScenario()]);
  const [busy, setBusy] = useState(false);
  const upd = (i, patch, probPatch) => setScenarios((s) => s.map((x, j) => j === i ? { ...x, ...patch, probability: { ...x.probability, ...(probPatch || {}) } } : x));
  const submit = async () => {
    if (!title.trim()) return notify.error("Titre requis.");
    if (scenarios.some((s) => !s.label.trim() || !s.probability.source.trim())) return notify.error("Chaque scénario exige un label ET une source de probabilité (pas d'estimation non sourcée).");
    setBusy(true);
    try {
      await api.post("/workspace/decisions", {
        title: title.trim(), question, cost_chf: Number(cost) || 0,
        capital_at_risk_chf: capital === "" ? null : Number(capital),
        cost_of_capital_chf: Number(coc) || 0,
        market_implied_probability: marketP === "" ? null : Number(marketP),
        context: { system: "deal", ref_type: "deal", ref_id: dealId },
        scenarios: scenarios.map((s) => ({ ...s, payoff_chf: Number(s.payoff_chf) || 0, probability: { ...s.probability, value: Number(s.probability.value), confidence: Number(s.probability.confidence) } })),
      });
      notify.success("Décision créée (brouillon).");
      onDone();
    } catch (e) { notify.error(fmtError(e)); }
    setBusy(false);
  };
  return (
    <div className="noam-card p-3 space-y-2" data-testid="decision-form">
      <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Titre de la décision…" className="noam-input text-xs w-full" data-testid="decision-title" />
      <div className="flex gap-2 flex-wrap">
        <input value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="Question posée (optionnel)…" className="noam-input text-xs flex-1 min-w-[200px]" />
        <input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="noam-input text-xs w-32" data-testid="decision-cost" title="Coûts de transaction (CHF)" placeholder="Coûts TX" />
        <input type="number" value={coc} onChange={(e) => setCoc(e.target.value)} className="noam-input text-xs w-32" title="Coût du capital (CHF)" placeholder="Coût capital" />
        <input type="number" value={capital} onChange={(e) => setCapital(e.target.value)} className="noam-input text-xs w-32" data-testid="decision-capital" title="Capital exposé (CHF)" placeholder="Capital exposé" />
        <input type="number" step="0.05" min="0" max="1" value={marketP} onChange={(e) => setMarketP(e.target.value)} className="noam-input text-xs w-28" title="Probabilité implicite marché (0-1)" placeholder="P. marché" />
      </div>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500 pt-1">Scénarios (probabilités sourcées, Σ = 1)</div>
      <div className="hidden lg:grid grid-cols-8 gap-1.5 px-2 text-[9px] uppercase tracking-widest text-zinc-600">
        <span className="col-span-2">Scénario</span><span>Probabilité</span><span>Payoff CHF</span>
        <span>Source du montant</span><span>Source proba *</span><span className="col-span-2">Méthode · Confiance</span>
      </div>
      {scenarios.map((s, i) => (
        <div key={i} className="grid grid-cols-2 lg:grid-cols-8 gap-1.5 p-2 rounded bg-white/[0.03]" data-testid={`decision-scenario-${i}`}>
          <input value={s.label} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Scénario…" className="noam-input text-[10px] lg:col-span-2" data-testid={`decision-scenario-label-${i}`} />
          <input type="number" step="0.05" min="0" max="1" value={s.probability.value} onChange={(e) => upd(i, {}, { value: e.target.value })} className="noam-input text-[10px]" title="Probabilité (0-1)" data-testid={`decision-scenario-prob-${i}`} />
          <input type="number" value={s.payoff_chf} onChange={(e) => upd(i, { payoff_chf: e.target.value })} placeholder="Payoff CHF" className="noam-input text-[10px]" title="Payoff CHF" data-testid={`decision-scenario-payoff-${i}`} />
          <input value={s.payoff_source || ""} onChange={(e) => upd(i, { payoff_source: e.target.value })} placeholder="Source du montant" className="noam-input text-[10px]" title="Provenance de l'hypothèse économique" data-testid={`decision-scenario-payoffsrc-${i}`} />
          <input value={s.probability.source} onChange={(e) => upd(i, {}, { source: e.target.value })} placeholder="Source proba *" className="noam-input text-[10px]" data-testid={`decision-scenario-source-${i}`} />
          <div className="flex gap-1 lg:col-span-2">
            <select value={s.probability.method} onChange={(e) => upd(i, {}, { method: e.target.value })} className="noam-input text-[10px] flex-1 min-w-0">
              {METHODS.map((m) => <option key={m}>{m}</option>)}
            </select>
            <input type="number" step="0.1" min="0" max="1" value={s.probability.confidence} onChange={(e) => upd(i, {}, { confidence: e.target.value })} className="noam-input text-[10px] w-16" title="Confiance (0-1)" />
            <button onClick={() => setScenarios((x) => x.filter((_, j) => j !== i))} className="btn-ghost p-1 text-[#E24B4A]"><Trash2 className="w-3 h-3" /></button>
          </div>
        </div>
      ))}
      <MemoryRecallPanel system="deal" p={scenarios.reduce((acc, s) => acc + ((Number(s.payoff_chf) || 0) > 0 ? Number(s.probability.value) || 0 : 0), 0) || null} />
      <div className="flex justify-between items-center">
        <button onClick={() => setScenarios((s) => [...s, emptyScenario()])} className="btn-ghost text-[10px]"><Plus className="w-3 h-3" /> Scénario</button>
        <span className="text-[9px] text-zinc-600" title="Capital Efficiency = AdjEV ÷ Capital exposé">Cap. eff. = AdjEV ÷ Capital exposé — calculé à la création</span>
        <button onClick={submit} disabled={busy} className="btn-primary text-xs" data-testid="decision-create">{busy ? "…" : "Créer la décision"}</button>
      </div>
    </div>
  );
}

function DecisionCard({ d, onReload }) {
  const [verdict, setVerdict] = useState(d.recommendation || "PURSUE");
  const [rationale, setRationale] = useState("");
  const [outcomeScenario, setOutcomeScenario] = useState("");
  const [payoffReal, setPayoffReal] = useState("");
  const [reestimating, setReestimating] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const eco = d.economics || {};
  const decide = async () => {
    if (!rationale.trim()) return notify.error("Justification requise.");
    try {
      await api.post(`/workspace/decisions/${d.id}/decide`, { verdict, rationale });
      notify.success(`Décision figée : ${verdict} (snapshot immuable).`); onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  const close = async () => {
    try {
      await api.post(`/workspace/decisions/${d.id}/outcome`, {
        scenario_id_realized: outcomeScenario || null,
        payoff_realized_chf: payoffReal === "" ? null : Number(payoffReal),
      });
      notify.success("Outcome enregistré — la boucle de calibration apprend."); onReload();
    } catch (e) { notify.error(fmtError(e)); }
  };
  return (
    <div className="noam-card p-3 space-y-2" data-testid={`decision-card-${d.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <Scale3d className="w-3.5 h-3.5 text-[#7F77DD]" />
        <span className="text-xs font-medium text-zinc-100 flex-1">{d.title}</span>
        <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${d.status === "draft" ? "border-white/15 text-zinc-400" : d.status === "decided" ? "border-[#378ADD]/40 text-[#378ADD]" : "border-[#1D9E75]/40 text-[#1D9E75]"}`}>{d.status}</span>
        {d.decision && <span className={`text-[9px] px-1.5 py-0.5 rounded-full border ${VERDICT_STYLE[d.decision.verdict]}`} data-testid={`decision-verdict-${d.id}`}>{d.decision.verdict}</span>}
        {(d.estimation_version || 1) > 1 && (
          <button onClick={() => setShowHistory(!showHistory)} className="text-[9px] px-1.5 py-0.5 rounded-full border border-[#7F77DD]/40 text-[#7F77DD] flex items-center gap-1" data-testid={`decision-version-${d.id}`}>
            <History className="w-2.5 h-2.5" /> v{d.estimation_version}
          </button>
        )}
        {d.status !== "closed" && (
          <button onClick={() => setReestimating(!reestimating)} className="btn-ghost text-[9px]" data-testid={`decision-reestimate-btn-${d.id}`}>Ré-estimer</button>
        )}
      </div>
      {showHistory && <EstimationHistory decision={d} />}
      {reestimating && <ReestimateForm decision={d} onDone={() => { setReestimating(false); onReload(); }} />}
      {eco.ev_chf != null && (
        <div className="flex gap-3 text-[10px] text-zinc-400 flex-wrap" data-testid={`decision-eco-${d.id}`}>
          <span>P(succès) <b className="text-zinc-200">{Math.round((eco.p_success || 0) * 100)}%</b></span>
          <span>EV <b className="text-zinc-200">{fmtCHF(eco.ev_chf)}</b></span>
          <span>AdjEV <b className="text-zinc-200">{fmtCHF(eco.adjusted_ev_chf)}</b></span>
          <span>Downside <b className="text-[#E24B4A]">{fmtCHF(eco.downside_chf)}</b></span>
          {eco.capital_at_risk_chf != null && <span>Capital exposé <b className="text-zinc-200">{fmtCHF(eco.capital_at_risk_chf)}</b></span>}
          <span>Confiance <b className="text-zinc-200">{Math.round((eco.confidence_avg || 0) * 100)}%</b></span>
          {eco.market_implied_probability != null && <span>Marché <b className="text-zinc-200">{Math.round(eco.market_implied_probability * 100)}%</b></span>}
          {eco.edge != null && <span>Edge <b className={eco.edge >= 0 ? "text-[#1D9E75]" : "text-[#E24B4A]"}>{eco.edge >= 0 ? "+" : ""}{Math.round(eco.edge * 100)} pts</b></span>}
          {eco.capital_efficiency != null && <span title="Capital Efficiency = AdjEV ÷ Capital exposé : rendement ajusté par franc de capital à risque" className="cursor-help border-b border-dotted border-zinc-600">Cap. eff. <b className="text-zinc-200">{eco.capital_efficiency}</b></span>}
          {eco.breakeven_p_success != null && <span title="P(succès) sous laquelle AdjEV devient négatif">Break-even <b className="text-zinc-200">{Math.round(eco.breakeven_p_success * 100)}%</b></span>}
          {d.status === "draft" && d.recommendation && <span>Noam suggère : <b className={`${VERDICT_STYLE[d.recommendation]} px-1 rounded`}>{d.recommendation}</b></span>}
        </div>
      )}
      {(eco.flags || []).includes("POSITIVE_EV_LOW_CONFIDENCE") && (
        <div className="text-[10px] px-2 py-1 rounded border border-[#EF9F27]/40 text-[#EF9F27] bg-[#EF9F27]/10" data-testid={`decision-flag-${d.id}`}>
          ⚠ EV positive mais confiance faible — l'estimation est fragile, ne pas la présenter comme une certitude.
        </div>
      )}
      <div className="space-y-0.5">
        {(d.scenarios || []).map((s) => (
          <div key={s.id} className="text-[10px] text-zinc-500 flex gap-2">
            <span className="font-mono text-zinc-300">{Math.round(s.probability.value * 100)}%</span>
            <span className="flex-1 truncate">{s.label}</span>
            <span>{fmtCHF(s.payoff_chf)}</span>
            <span className="text-zinc-600 truncate max-w-[120px]" title={`${s.probability.method} · ${s.probability.source}`}>{s.probability.method}</span>
          </div>
        ))}
      </div>
      {d.status === "draft" && (
        <div className="flex gap-1.5 items-center pt-1 border-t border-white/5">
          <select value={verdict} onChange={(e) => setVerdict(e.target.value)} className="noam-input text-[10px] w-24" data-testid={`decision-verdict-select-${d.id}`}>
            {["PURSUE", "HOLD", "PASS"].map((v) => <option key={v}>{v}</option>)}
          </select>
          <input value={rationale} onChange={(e) => setRationale(e.target.value)} placeholder="Justification…" className="noam-input text-[10px] flex-1" data-testid={`decision-rationale-${d.id}`} />
          <button onClick={decide} className="btn-primary text-[10px]" data-testid={`decision-decide-${d.id}`}><CheckCircle2 className="w-3 h-3" /> Décider</button>
        </div>
      )}
      {d.status === "decided" && (
        <div className="flex gap-1.5 items-center pt-1 border-t border-white/5">
          <select value={outcomeScenario} onChange={(e) => setOutcomeScenario(e.target.value)} className="noam-input text-[10px] flex-1" data-testid={`decision-outcome-scenario-${d.id}`}>
            <option value="">Scénario réalisé…</option>
            {(d.scenarios || []).map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <input type="number" value={payoffReal} onChange={(e) => setPayoffReal(e.target.value)} placeholder="Payoff réel CHF" className="noam-input text-[10px] w-32" data-testid={`decision-payoff-real-${d.id}`} />
          <button onClick={close} className="btn-primary text-[10px]" data-testid={`decision-outcome-${d.id}`}><Flag className="w-3 h-3" /> Outcome</button>
        </div>
      )}
      {d.outcome && (
        <div className="text-[10px] text-zinc-500">Réel : {d.outcome.payoff_realized_chf != null ? fmtCHF(d.outcome.payoff_realized_chf) : "—"} · enregistré {new Date(d.outcome.recorded_at).toLocaleDateString("fr-CH")}</div>
      )}
    </div>
  );
}

export function DealDecisions({ dealId, onReload }) {
  const [items, setItems] = useState(null);
  const [creating, setCreating] = useState(false);
  const load = () => {
    api.get(`/workspace/decisions?ref_id=${dealId}`).then((r) => setItems(r.data.items)).catch((e) => notify.error(fmtError(e)));
    if (onReload) onReload(); // rafraîchit la timeline du Deal 360 parent
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dealId]);
  return (
    <div className="space-y-3" data-testid="deal360-decisions">
      <div className="flex justify-end">
        <button onClick={() => setCreating(!creating)} className="btn-primary text-xs" data-testid="deal360-new-decision">
          <Scale3d className="w-3.5 h-3.5" /> Nouvelle décision
        </button>
      </div>
      {creating && <DecisionForm dealId={dealId} onDone={() => { setCreating(false); load(); }} />}
      {items && items.length === 0 && !creating && (
        <div className="p-8 text-center text-xs text-zinc-600 border border-dashed border-white/10 rounded-lg" data-testid="deal360-decisions-empty">
          Aucune décision journalisée pour ce deal. Créez la première : scénarios, probabilités sourcées, EV — puis décidez et mesurez.
        </div>
      )}
      {(items || []).map((d) => <DecisionCard key={d.id} d={d} onReload={load} />)}
    </div>
  );
}
